import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  HStack,
  MenuItem,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { useCreditFacilityExpensesDelete } from "features/credit-facility/expenses/CreditFacilityExpensesDelete";
import {
  getCreditFacilityExpenseDetailsTableRetrieveQueryKey,
  getCreditFacilityInterestExpenseEventsRetrieveQueryKey,
  useCreditFacilityInterestExpensePost,
  useCreditFacilityInterestExpenseRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import {
  useFundFamilyEventPendingToCompleteUpdate,
  useFundFamilyEventRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  CardSection,
  CardSectionBody,
  CardSectionGroup,
} from "shared/components/card/CardSection";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { RouteLink } from "shared/components/link/RouteLink";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { ArrowUpRightIcon } from "shared/icons/ArrowUpRightIcon";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatPercentage } from "shared/utils/formattingUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export const CreditFacilityPostExpensePage: React.FC = () => {
  const { t } = useTranslation(["common", "creditFacility", "enums"]);
  const { creditFacilityId, fundFamilyId, fundFamilyEventId } =
    useRequiredStringParams();

  const { mutateAsync: updateEventToComplete } =
    useFundFamilyEventPendingToCompleteUpdate();

  const { data: interestExpenses } =
    useCreditFacilityInterestExpenseRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      fundFamilyEventId,
    );

  const { data: fundFamilyEvent } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutateAsync: postExpenses } = useCreditFacilityInterestExpensePost();

  const baseRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId });

  const expenseLoanRoute = (loanId: string) =>
    baseRoute.event({ fundFamilyEventId }).expenses({}).loan({ loanId });

  const backToSummary = useCallback(() => {
    navigate(baseRoute.expenses({}).$);
  }, [navigate, baseRoute]);

  const finalizeEvent = useCallback(async () => {
    await updateEventToComplete({
      fundFamilyId,
      fundFamilyEventId,
    });
    await queryClient.invalidateQueries({
      queryKey: getCreditFacilityInterestExpenseEventsRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
    });
    await queryClient.invalidateQueries({
      queryKey: getCreditFacilityExpenseDetailsTableRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
    });
    backToSummary();
  }, [
    fundFamilyId,
    fundFamilyEventId,
    updateEventToComplete,
    backToSummary,
    creditFacilityId,
    queryClient,
  ]);

  const confirmDeleteEvent = useCreditFacilityExpensesDelete(
    fundFamilyId,
    creditFacilityId,
    backToSummary,
  );

  const badge = getCapitalActivityEventStateBadge(fundFamilyEvent.state, t);

  // there is actually no form on this page but we still use our form footer component to take advantage of loading and error handling
  const form = useForm();

  return (
    <Form
      onSuccess={finalizeEvent}
      onSubmit={() =>
        postExpenses({ fundFamilyId, creditFacilityId, fundFamilyEventId })
      }
      form={form}
    >
      <PageContent isFullHeight={true}>
        <PageHeader
          title={t("creditFacility:postExpense.title")}
          documentTitle={
            fundFamilyEvent.state == FundFamilyEventStateMachineFieldsEnum.draft
              ? t("creditFacility:postExpense.workflow")
              : t("creditFacility:postExpense.dailyBreakdown")
          }
          badge={
            <div>
              <Badge>
                {formatIsoDateToDisplay(interestExpenses.period_start)}-
                {formatIsoDateToDisplay(interestExpenses.period_end)}
              </Badge>
              {badge && <Badge colorScheme={badge.type}>{badge.text}</Badge>}
            </div>
          }
          backButton={{
            title: t("creditFacility:title"),
            path: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .creditFacilities({})
              .creditFacility({ creditFacilityId })
              .expenses({}).$,
          }}
          actions={
            fundFamilyEvent.state == "draft" && (
              <HStack>
                <Restricted permission={PermissionTypeEnum.approve}>
                  <FormSubmitButton>
                    {t("creditFacility:postExpense.type")}
                  </FormSubmitButton>
                </Restricted>
                <Restricted permission={PermissionTypeEnum.config}>
                  <PageMenu>
                    <MenuItem
                      className="destructive"
                      onClick={() => confirmDeleteEvent({ fundFamilyEventId })}
                    >
                      {t("common:discardDraft")}
                    </MenuItem>
                  </PageMenu>
                </Restricted>
              </HStack>
            )
          }
        />
        <PageBody>
          {Object.entries(interestExpenses.loan_expenses).map(
            ([loanId, expense], index) => (
              <HStack spacing="0" mb="16px" key={index}>
                <Card variant="stat" w="100%" gap="0px" flexDirection="row">
                  <VStack alignItems="stretch" flex={1}>
                    <CardHeader>
                      <HStack gap="8px" mt="16px">
                        <Text fontSize="18px">{expense.loan_name}</Text>
                        <Badge>
                          {t("creditFacility:postExpense.badges.calculated")}
                        </Badge>
                      </HStack>
                    </CardHeader>
                    <CardBody>
                      <CardSectionGroup flexWrap="wrap">
                        <CardSection flexGrow="1">
                          <CardSectionBody alignItems="stretch">
                            <StatGroup
                              alignItems="center"
                              paddingTop="0px !important"
                            >
                              <Stat>
                                <StatNumber width="360px">
                                  {Number(expense.updated_total_expense) ? (
                                    <HStack>
                                      <Text
                                        fontSize="20px"
                                        fontWeight="400"
                                        textDecoration="line-through"
                                      >
                                        {formatMoney(expense.total_expense)}
                                      </Text>
                                      <Text fontSize="20px" fontWeight="400">
                                        →
                                      </Text>
                                      <Text fontSize="20px" fontWeight="400">
                                        {formatMoney(
                                          expense.updated_total_expense,
                                        )}
                                      </Text>
                                    </HStack>
                                  ) : (
                                    <Text fontSize="20px" fontWeight="400">
                                      {formatMoney(expense.total_expense)}
                                    </Text>
                                  )}
                                </StatNumber>
                                <StatLabel>
                                  {t(
                                    "creditFacility:postExpense.totalInterestExpenseForPeriod",
                                  )}
                                </StatLabel>
                              </Stat>
                              <Divider orientation="vertical" />
                              <Stat>
                                <StatNumber>
                                  {Number(
                                    expense.updated_average_interest_rate,
                                  ) > 0 &&
                                  formatPercentage(
                                    expense.updated_average_interest_rate ??
                                      "0",
                                  ) !==
                                    formatPercentage(
                                      expense.average_interest_rate,
                                    ) ? (
                                    <HStack>
                                      <Text
                                        fontSize="14px"
                                        fontWeight="400"
                                        textDecoration="line-through"
                                      >
                                        {formatPercentage(
                                          expense.average_interest_rate,
                                        )}
                                      </Text>
                                      <Text fontSize="14px" fontWeight="400">
                                        →
                                      </Text>
                                      <Text fontSize="14px" fontWeight="400">
                                        {formatPercentage(
                                          expense.updated_average_interest_rate ??
                                            "0",
                                        )}
                                      </Text>
                                    </HStack>
                                  ) : formatPercentage(
                                      expense.updated_average_interest_rate ??
                                        "0",
                                    ) ==
                                      formatPercentage(
                                        expense.average_interest_rate,
                                      ) &&
                                    Number(expense.average_interest_rate) >
                                      0 ? (
                                    <Tooltip
                                      label="Adjustment not visible when rounding to two decimal points."
                                      placement="top"
                                    >
                                      <HStack alignItems="center">
                                        <Text fontSize="14px" fontWeight="400">
                                          {formatPercentage(
                                            expense.average_interest_rate,
                                          )}
                                        </Text>
                                        <InfoCircleIcon />
                                      </HStack>
                                    </Tooltip>
                                  ) : (
                                    <Text fontSize="14px" fontWeight="400">
                                      {formatPercentage(
                                        expense.average_interest_rate,
                                      )}
                                    </Text>
                                  )}
                                </StatNumber>
                                <StatLabel>
                                  {t(
                                    "creditFacility:postExpense.dailyAverageAllInRate",
                                  )}
                                </StatLabel>
                              </Stat>
                              <Stat>
                                <StatNumber>
                                  <Text fontSize="14px" fontWeight="400">
                                    {formatMoney(
                                      expense.average_principal_outstanding,
                                    )}
                                  </Text>
                                </StatNumber>
                                <StatLabel>
                                  {t(
                                    "creditFacility:postExpense.dailyAveragePrincipalOutstanding",
                                  )}
                                </StatLabel>
                              </Stat>
                            </StatGroup>
                          </CardSectionBody>
                        </CardSection>
                      </CardSectionGroup>
                    </CardBody>
                  </VStack>
                  <HStack gap="16px" pr="24px">
                    <RouteLink to={expenseLoanRoute(loanId).details({}).$}>
                      {t("creditFacility:postExpense.dailyBreakdown")}
                      <ArrowUpRightIcon />
                    </RouteLink>
                    {fundFamilyEvent.state == "draft" && (
                      <Button
                        onClick={() =>
                          navigate(expenseLoanRoute(loanId).adjust({}).$)
                        }
                      >
                        {t("creditFacility:postExpense.adjust")}
                      </Button>
                    )}
                  </HStack>
                </Card>
              </HStack>
            ),
          )}
        </PageBody>
      </PageContent>
    </Form>
  );
};
